export const ORGANIZE_LIMITS = Object.freeze({
  bytes: 50 * 1024 * 1024,
  pages: 100
});

export function validateOrganizeFile(file) {
  if (!file) return "Choose a PDF to continue.";
  if (file.size > ORGANIZE_LIMITS.bytes) return "This PDF is larger than the 50 MB limit.";
  const extension = file.name?.toLowerCase().endsWith(".pdf");
  const mime = (file.type || "").toLowerCase();
  if (!extension && !["application/pdf", "application/octet-stream", ""].includes(mime)) {
    return "Choose a file in PDF format.";
  }
  return null;
}

export function hasPdfHeader(bytes) {
  if (!bytes || bytes.length < 5) return false;
  return String.fromCharCode(...bytes.slice(0, 5)) === "%PDF-";
}

export function validateOrganizePageCount(count) {
  if (!Number.isInteger(count) || count < 1) return "This PDF does not contain any pages.";
  if (count > ORGANIZE_LIMITS.pages) {
    return `This PDF has ${count} pages. The current limit is ${ORGANIZE_LIMITS.pages}.`;
  }
  return null;
}

export function createPageItems(rotations) {
  return rotations.map((rotation, sourceIndex) => ({
    id: `page-${sourceIndex + 1}`,
    sourceIndex,
    originalRotation: normalizeRotation(rotation),
    rotation: normalizeRotation(rotation),
    selected: false,
    thumbnailUrl: null
  }));
}

export function movePage(items, fromIndex, toIndex) {
  if (fromIndex < 0 || fromIndex >= items.length || toIndex < 0 || toIndex >= items.length || fromIndex === toIndex) {
    return [...items];
  }
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

export function setAllSelected(items, selected) {
  return items.map(item => ({ ...item, selected }));
}

export function togglePageSelection(items, id) {
  return items.map(item => item.id === id ? { ...item, selected: !item.selected } : item);
}

export function rotateSelectedPages(items, delta) {
  return items.map(item => item.selected
    ? { ...item, rotation: normalizeRotation(item.rotation + delta) }
    : item);
}

export function deleteSelectedPages(items) {
  const selected = items.filter(item => item.selected).length;
  if (!selected) return { items: [...items], error: "Select at least one page first." };
  if (selected === items.length) return { items: [...items], error: "Keep at least one page in the document." };
  return { items: items.filter(item => !item.selected), error: null };
}

export function normalizeRotation(value) {
  return ((Number(value || 0) % 360) + 360) % 360;
}

function basePdfName(value) {
  const clean = (value || "document.pdf").trim().replace(/[\\/:*?"<>|]+/g, "-");
  const withoutExtension = clean.toLowerCase().endsWith(".pdf") ? clean.slice(0, -4) : clean;
  return withoutExtension || "document";
}

export function organizedFileName(value) {
  return `${basePdfName(value)}-organized.pdf`;
}

export function selectedPagesFileName(value) {
  return `${basePdfName(value)}-selected-pages.pdf`;
}

export function rotatedFileName(value) {
  return `${basePdfName(value)}-rotated.pdf`;
}

export function pagesRemovedFileName(value) {
  return `${basePdfName(value)}-pages-removed.pdf`;
}
